import React, { Component } from 'react';
import { connect } from 'react-redux';
import { apiGet } from 'helpers/ApiHelper';
import Auth from 'j-toker';

import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Card, CardTitle, CardActions, CardText } from 'material-ui/Card';
import FlatButton from 'material-ui/FlatButton';
import CtaText from 'components/ui/CtaCard';

import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import {
  toFriendlyDateString,
  toFriendlyDateTimeString,
  toFriendlyDateTimeRange,
} from 'helpers/Formatter';

import { showFailure } from 'actions/notificationActions';
import { explosionCelebration } from 'actions/notificationActions';
import { rsvpForEvent } from 'actions/tenantProfileActions';

import moment from 'moment';

function base64ToBlob(base64String, type = 'application/octet-stream') {
  const bytes = window.atob(base64String);
  const bytesSize = bytes.length;
  const blobPart = new Uint8Array(bytesSize);

  for (let index = 0; index < bytesSize; index++) {
    blobPart[index] = bytes.charCodeAt(index);
  }

  return new Blob([blobPart], { type: type });
}

let EventAndNoticeInfo = class EventAndNoticeInfo extends Component {
  state = {
    downloadingImage: false,
    downloadingAttachment: false,
  };
  rsvpForEvent = () => {
    this.props.rsvpForEvent(this.props.eventAndNotice.id).then(() => {
      this.props.explosionCelebration();
    });
  };

  downloadAttachment = () => {
    const { eventAndNotice } = this.props;
    const noticeId = eventAndNotice.id;

    this.setState({
      downloadingAttachment: true,
    });
    apiGet(Auth.getApiUrl() + `/leads/notices/${noticeId}/external_attachment`, {
      method: 'GET',
    })
      .then((response) => {
        const blob = base64ToBlob(response.data.bytes, response.data.contentType);
        const url = URL.createObjectURL(blob);

        this.props.base64String(url);

        this.setState({
          downloadingAttachment: false,
        });
      })
      .catch(() => {
        this.props.showFailure(getTranslatedString('couldNotGetAttachment'));
        this.setState({
          downloadingAttachment: false,
        });
      });
  };

  downloadImage = () => {
    const { eventAndNotice } = this.props;
    const noticeId = eventAndNotice.id;
    this.setState({
      downloadingImage: true,
    });
    apiGet(Auth.getApiUrl() + `/leads/notices/${noticeId}/external_image`, {
      method: 'GET',
    })
      .then((response) => {
        const linkSource = `data:${response.data.contentType};base64,${response.data.bytes}`;
        this.props.base64String(linkSource);
        this.setState({
          downloadingImage: false,
        });
      })
      .catch(() => {
        this.props.showFailure(getTranslatedString('couldNotGetAttachment'));
        this.setState({
          downloadingImage: false,
        });
      });
  };

  render() {
    const style = {
      card: {
        color: '#424242 !important',
        fontFamily: '"Circular", sans-serif !important',
        height: '100%',
        overflow: 'hidden',
        marginTop: '15px',
        maxWidth: '600px',
      },
      cardHeaderStyle: {
        fontFamily: 'Circular-Bold',
        fontSize: '18px',
      },
    };

    const eventOrNotice = this.props.eventAndNotice;
    const image = eventOrNotice.image || eventOrNotice.photo;

    // Note that you can not rsvp to an event that has already started
    const displayRSVPButton =
      eventOrNotice.can_rsvp &&
      moment(eventOrNotice.start_date) >= moment() &&
      !eventOrNotice.has_rsvped;

    return (
      <Card style={style.card} className="card" initiallyExpanded={true}>
        {image && (
          <img
            style={{
              width: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
            }}
            src={image}
            alt={eventOrNotice.name}
          />
        )}
        <CardTitle
          title={eventOrNotice.name}
          subtitle={
            <div>
              <div>
                {eventOrNotice.start_date && (
                  <p>
                    {getTranslatedString('from')}{' '}
                    {toFriendlyDateTimeString(eventOrNotice.start_date, {
                      language: this.props.currentLanguage,
                    })}{' '}
                    {getTranslatedString('to')}{' '}
                    {toFriendlyDateTimeString(eventOrNotice.end_date, {
                      language: this.props.currentLanguage,
                    })}
                  </p>
                )}
                {eventOrNotice.post_date && (
                  <p style={{ margin: 0 }}>
                    {getTranslatedString('postedOn')}{' '}
                    {toFriendlyDateString(eventOrNotice.post_date, {
                      language: this.props.currentLanguage,
                    })}
                  </p>
                )}
              </div>
            </div>
          }
          style={{ padding: '15px 16px 5px', color: colors.DARK_GRAY }}
          titleStyle={{ fontFamily: 'Circular-Bold' }}
        />
        <CardText style={{ padding: '5px 16px' }}>
          <div className="row">
            <h4 style={{ marginBottom: '2px' }}> {getTranslatedString('details')} </h4>
            <p style={{ whiteSpace: 'pre-line', wordWrap: 'break-word' }}>
              {eventOrNotice.description}
            </p>
          </div>
          {eventOrNotice.link && (
            <div className="row">
              <a href={eventOrNotice.link} target="_blank" rel="noopener noreferrer">
                {eventOrNotice.link_alt ? eventOrNotice.link_alt : eventOrNotice.name}
              </a>
            </div>
          )}
          {eventOrNotice.effective_dates && eventOrNotice.effective_dates.length > 0 && (
            <div className="row">
              <h5>{getTranslatedString('effective')}</h5>
              {eventOrNotice.effective_dates.map((date, index) => (
                <p>
                  {toFriendlyDateTimeRange(date[0], date[1], {
                    language: this.props.currentLanguage,
                  })}
                </p>
              ))}
            </div>
          )}
          {eventOrNotice.attachment && (
            <div className="row">
              <h4 style={{ marginBottom: '2px' }}> {getTranslatedString('attachments')} </h4>
              <a href={eventOrNotice.attachment} target="_blank" rel="noopener noreferrer">
                {getTranslatedString('importantDocument')}
              </a>
            </div>
          )}
          {eventOrNotice.attachments && eventOrNotice.attachments.length > 0 && (
            <h4 style={{ marginBottom: '2px' }}> {getTranslatedString('attachments')} </h4>
          )}
          {eventOrNotice.attachments?.map((attachment, index) => (
            <div className="row" key={`${attachment.name}.${index}`}>
              <a href={attachment.preview} target="_blank" rel="noopener noreferrer">
                {attachment.name}
              </a>
            </div>
          ))}

          {eventOrNotice.has_external_attachment && (
            <div className="row">
              <h4 style={{ marginBottom: '2px' }}> {getTranslatedString('attachments')} </h4>
              <CtaText
                style={{ padding: 0 }}
                type="button"
                onClick={this.downloadAttachment}
                disabled={this.state.downloadingAttachment}
              >
                {getTranslatedString('importantDocument')}{' '}
                {this.state.downloadingAttachment && <Icon component={LoadingOutlined} spin />}
              </CtaText>
            </div>
          )}

          {eventOrNotice.has_external_image && (
            <div className="row">
              <h4 style={{ marginBottom: '2px' }}> {getTranslatedString('images')} </h4>
              <CtaText
                style={{ padding: 0 }}
                type="button"
                onClick={this.downloadImage}
                disabled={this.state.downloadingImage}
              >
                {getTranslatedString('viewImage')}{' '}
                {this.state.downloadingImage && <Icon component={LoadingOutlined} spin />}
              </CtaText>
            </div>
          )}
          {eventOrNotice.has_rsvped && (
            <div className="row" style={{ textAlign: 'center' }}>
              <h5> {getTranslatedString('events.rsvp')} </h5>
              <button
                type="button"
                className="link-button"
                onClick={() => this.props.rsvpForEvent(eventOrNotice.id)}
              >
                {getTranslatedString('events.cancelRSVP')}
              </button>
            </div>
          )}
          {eventOrNotice.attach_management_contact && (
            <div className="row" style={{ textAlign: 'center' }}>
              <h5>
                {getTranslatedString('events.contact', {
                  contactNumber: this.props.contactNumber,
                })}
              </h5>
            </div>
          )}
        </CardText>
        {displayRSVPButton && (
          <CardActions style={{ textAlign: 'center', padding: '0 15px 15px' }}>
            <FlatButton
              id="rsvp-site-event-button"
              label="RSVP"
              onClick={this.rsvpForEvent}
              primary={true}
              disabled={this.props.disableViewButton || false}
              backgroundColor={this.props.disableViewButton ? colors.DARK_GRAY : AccentColor()}
              hoverColor="grey"
              fullWidth
              style={{
                color: 'white',
                margin: 'auto',
              }}
            />
          </CardActions>
        )}
      </Card>
    );
  }
};

export default connect(
  (store) => ({
    contactNumber: store.tenantProfile.buildingCommunity.contact_number,
    currentLanguage: store.locale.currentLanguage,
  }),
  { rsvpForEvent, explosionCelebration, showFailure }
)(EventAndNoticeInfo);
